import React, { useState } from 'react';
import { TruckVisualizerHandle } from './TruckVisualizer';
import { TruckSpecs, PlacedPallet, UnitSystem, LoadResult } from '../types';
import { generateLoadReport } from '../utils/pdfGenerator';
import { X, FileDown, Camera } from 'lucide-react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  visualizerRef: React.RefObject<TruckVisualizerHandle | null>;
  truck: TruckSpecs;
  pallets: PlacedPallet[];
  stats: LoadResult;
  unitSystem: UnitSystem;
}

const ExportModal: React.FC<ExportModalProps> = ({ 
  isOpen, onClose, visualizerRef, truck, pallets, stats, unitSystem 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleDownload = () => {
    setIsGenerating(true);
    // Short timeout to allow UI update and screenshot capture
    setTimeout(() => {
        let screenshot = '';
        if (visualizerRef.current) {
            screenshot = visualizerRef.current.takeScreenshot();
        }
        
        generateLoadReport(
            truck, 
            pallets, 
            stats.totalWeight, 
            stats.usedLength, 
            screenshot, 
            unitSystem
        );
        setIsGenerating(false);
        onClose();
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-800">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <FileDown className="text-green-400 w-5 h-5" />
            Export to PDF
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
            <div className="text-center p-6 bg-slate-800/50 rounded-lg border border-slate-700 border-dashed">
                <Camera className="w-12 h-12 text-blue-400 mx-auto mb-3 opacity-50" />
                <p className="text-sm text-slate-300">
                    The report will include a screenshot of the <strong>current 3D view</strong>.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                    Tip: Use the camera controls on the right of the main screen to set up your preferred angle before exporting.
                </p>
            </div>

            <div className="space-y-2 bg-slate-800 p-4 rounded border border-slate-700">
                <div className="flex justify-between text-sm text-slate-300">
                    <span>Items to Export:</span>
                    <span className="text-white font-bold">{pallets.length}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-300">
                    <span>Truck Type:</span>
                    <span className="text-white font-bold">{truck.name}</span>
                </div>
            </div>

            <button 
                onClick={handleDownload}
                disabled={isGenerating}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-green-900/20 transition disabled:opacity-50"
            >
                {isGenerating ? 'Generating...' : 'Download PDF Report'}
            </button>
        </div>

      </div>
    </div>
  );
};

export default ExportModal;